/**
 * Конфиг API/OAuth по домену.
 * Один бандл для всех доменов: выбор по window.location.hostname.
 */

import { buildDomainServiceUrl } from "@/shared/config/service-url"
import { env } from "./env"

export { resolveAbsoluteUrl } from "@medme/api-client"

export type AppConfig = {
	apiUrl: string
	marketingApiUrl: string
	organizationApiUrl: string
	oauthUrl: string
	clientId: string
}

const CLIENT_ID = "6a20157cd18d13dd6c0b9b6c"

/**
 * Кастомные клиенты с нестандартной субдоменной структурой.
 * Проверяются до применения универсального алгоритма.
 */
const CUSTOM_DOMAIN_OVERRIDES: Record<string, AppConfig> = {
	"rzd-med.ru": {
		apiUrl: "https://stats-api.med-me.rzd-med.ru",
		marketingApiUrl: "https://marketing-api.med-me.rzd-med.ru",
		organizationApiUrl: "https://organization-api.med-me.rzd-med.ru/api/v1",
		oauthUrl: "https://oauth.med-me.rzd-med.ru",
		clientId: CLIENT_ID,
	},
	"invitro.ru": {
		apiUrl: "https://stats-api-gbooking.invitro.ru",
		marketingApiUrl: "https://marketing-api-gbooking.invitro.ru",
		organizationApiUrl: "https://organization-api-gbooking.invitro.ru/api/v1",
		oauthUrl: "https://oauth2-gbooking.invitro.ru",
		clientId: CLIENT_ID,
	},
}

function getHostname(): string {
	if (typeof window === "undefined") return ""
	return window.location.hostname
}

function getPortSuffix(): string {
	if (typeof window === "undefined") return ""
	const port = window.location.port
	return port ? `:${port}` : ""
}

function findCustomOverride(hostname: string): AppConfig | undefined {
	for (const [domain, config] of Object.entries(CUSTOM_DOMAIN_OVERRIDES)) {
		if (hostname === domain || hostname.endsWith(`.${domain}`)) {
			return config
		}
	}
	return undefined
}

function buildDynamicConfig(hostname: string): AppConfig {
	// All standard service origins share the Compose subdomain algorithm.
	const buildUrl = (sub: string) =>
		buildDomainServiceUrl(hostname, getPortSuffix().slice(1), sub)

	return {
		apiUrl: buildUrl("stats-api"),
		marketingApiUrl: buildUrl("marketing-api"),
		organizationApiUrl: `${buildUrl("organization-api")}/api/v1`,
		oauthUrl: buildUrl("oauthv2"),
		clientId: CLIENT_ID,
	}
}

/**
 * Возвращает конфиг для текущего домена. Без сетевых запросов.
 */
export const getAppConfig = (): AppConfig => {
	const hostname = getHostname()
	const config = findCustomOverride(hostname) ?? buildDynamicConfig(hostname)
	// Local deployments may route the Organization API through a same-origin proxy.
	return {
		...config,
		...(env.VITE_ORGANIZATION_API_URL
			? { organizationApiUrl: env.VITE_ORGANIZATION_API_URL }
			: {}),
	}
}

export const getOAuthRedirectUri = (): string => {
	if (typeof window === "undefined") {
		return "/"
	}
	return window.location.origin + window.location.pathname
}

export const buildOAuthAuthorizeUrl = (config?: AppConfig): string => {
	const resolved = config ?? getAppConfig()
	const params = new URLSearchParams({
		type: "web_server",
		redirect_uri: getOAuthRedirectUri(),
		response_type: "code",
		client_id: resolved.clientId,
	})
	return `${resolved.oauthUrl}/authorize?${params.toString()}`
}
